import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

const TwilioConfig = ({
  config,
  onConfigChange,
  onDelete,
}) => {
  const voices = [
    { id: 'alice', name: 'Alice (Female)' },
    { id: 'bob', name: 'Bob (Male)' },
    { id: 'charlie', name: 'Charlie (Male)' },
    { id: 'diana', name: 'Diana (Female)' },
  ];

  const voicemailDetectionOptions = [
    { value: 'detect', label: 'Detect Voicemail' },
    { value: 'detect_beep', label: 'Detect Beep' },
    { value: 'detect_words', label: 'Detect Words' },
    { value: 'greeting_end', label: 'Greeting End' },
    { value: 'disabled', label: 'Disabled' },
  ];

  const voicemailActions = [
    { value: 'hangup', label: 'Hang Up' },
    { value: 'leave_message', label: 'Leave Message' },
    { value: 'retry_later', label: 'Retry Later' },
  ];

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Twilio Configuration
      </Typography>

      {/* Voice Selection */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Voice</InputLabel>
        <Select
          value={config.voice_id || 'alice'}
          onChange={handleConfigChange('voice_id')}
          label="Voice"
        >
          {voices.map(voice => (
            <MenuItem key={voice.id} value={voice.id}>
              {voice.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Voice Name Display */}
      <TextField
        fullWidth
        margin="normal"
        label="Voice Name"
        value={config.voice_name || ''}
        onChange={handleConfigChange('voice_name')}
        helperText="Human-readable name for the selected voice"
      />

      {/* Voicemail Detection */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Voicemail Detection</InputLabel>
        <Select
          value={config.voicemail_detection || 'detect'}
          onChange={handleConfigChange('voicemail_detection')}
          label="Voicemail Detection"
        >
          {voicemailDetectionOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Voicemail Message */}
      <TextField
        fullWidth
        margin="normal"
        label="Voicemail Message"
        multiline
        rows={3}
        value={config.voicemail_message || ''}
        onChange={handleConfigChange('voicemail_message')}
        helperText="Message to leave on voicemail"
      />

      {/* Voicemail Action */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Voicemail Action</InputLabel>
        <Select
          value={config.voicemail_action || 'hangup'}
          onChange={handleConfigChange('voicemail_action')}
          label="Voicemail Action"
        >
          {voicemailActions.map(action => (
            <MenuItem key={action.value} value={action.value}>
              {action.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Max Duration */}
      <TextField
        fullWidth
        margin="normal"
        label="Max Duration (minutes)"
        type="number"
        value={config.max_duration || 5}
        onChange={handleConfigChange('max_duration')}
        inputProps={{ min: 1, max: 60 }}
      />

      {/* Retry Attempts */}
      <TextField
        fullWidth
        margin="normal"
        label="Retry Attempts"
        type="number"
        value={config.retry_attempts || 0}
        onChange={handleConfigChange('retry_attempts')}
        inputProps={{ min: 0, max: 3 }}
      />

      {/* Call Timeout */}
      <TextField
        fullWidth
        margin="normal"
        label="Call Timeout (seconds)"
        type="number"
        value={config.call_timeout || 30}
        onChange={handleConfigChange('call_timeout')}
        inputProps={{ min: 10, max: 300 }}
      />

      {/* Record Call */}
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={config.record_call ?? false}
              onChange={handleConfigChange('record_call')}
            />
          }
          label="Record Call"
        />
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        Twilio provides reliable voice calling with standard TTS capabilities and robust infrastructure.
      </Alert>

      {onDelete && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{ mt: 2 }}
        >
          Remove Voice Configuration
        </Button>
      )}
    </Box>
  );
};

TwilioConfig.propTypes = {
  config: PropTypes.object.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default TwilioConfig; 